type SentryModule = typeof import("@sentry/react");

let sentryModulePromise: Promise<SentryModule> | null = null;
let sentryInitialized = false;

const shouldEnableSentry = () => {
  if (typeof window === "undefined") return false;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  return Boolean(dsn) && (import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === "true");
};

const loadSentryModule = () => {
  if (!sentryModulePromise) {
    sentryModulePromise = import("@sentry/react");
  }

  return sentryModulePromise;
};

export const initSentry = async () => {
  if (!shouldEnableSentry() || sentryInitialized) {
    return;
  }

  const Sentry = await loadSentryModule();

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE || "development",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    release: __APP_VERSION__,
    enabled: true,
    beforeSend(event, hint) {
      const error = hint?.originalException;
      if (error && typeof error === "object" && "message" in error) {
        const message = String((error as Error).message);
        if (message.includes("chrome-extension://") || message.includes("moz-extension://")) {
          return null;
        }
      }

      if (typeof error === "string") {
        if (
          error.includes("Object Not Found Matching Id") &&
          error.includes("MethodName") &&
          error.includes("ParamCount")
        ) {
          return null;
        }
      }

      if (event.message) {
        if (
          event.message.includes("Object Not Found Matching Id") &&
          event.message.includes("MethodName") &&
          event.message.includes("ParamCount")
        ) {
          return null;
        }
      }

      return event;
    },
  });

  sentryInitialized = true;
};

const runWithSentry = async <Result>(callback: (sentry: SentryModule) => Result) => {
  if (!shouldEnableSentry()) {
    return null;
  }

  const sentry = await loadSentryModule();
  return callback(sentry);
};

export const addSentryBreadcrumb = (breadcrumb: Parameters<SentryModule["addBreadcrumb"]>[0]) => {
  void runWithSentry((sentry) => sentry.addBreadcrumb(breadcrumb));
};

export const captureSentryException = (
  error: unknown,
  captureContext?: Parameters<SentryModule["captureException"]>[1],
) => {
  void runWithSentry((sentry) => sentry.captureException(error, captureContext));
};

export const showSentryReportDialog = (options: Parameters<SentryModule["showReportDialog"]>[0]) => {
  void runWithSentry((sentry) => sentry.showReportDialog(options));
};

export const withSentry = runWithSentry;

export const setSentryUser = (user: { id?: string | null; email?: string | null } | null) => {
  void runWithSentry((sentry) => {
    if (!user) {
      sentry.setUser(null);
      return;
    }
    const { id, email } = user;
    sentry.setUser({
      id: id || undefined,
      email: email || undefined,
    });
  });
};
